import React, { useState, useEffect } from 'react';

const riskQuestions = [
  {
    question: "Guess the type of risk that involves uncertainties in achieving long-term objectives.",
    answer: "strategic risk",
    clue: "It relates to a company's overall strategy."
  },
  {
    question: "Guess the type of risk that arises from day-to-day business operations.",
    answer: "operational risk",
    clue: "It is linked to internal processes and systems."
  },
  {
    question: "Guess the type of risk related to financial transactions and reporting.",
    answer: "financial risk",
    clue: "It concerns monetary aspects and financial stability."
  },
  {
    question: "Guess the type of risk involving adherence to laws and regulations.",
    answer: "compliance risk",
    clue: "It focuses on legal and regulatory requirements."
  },
  {
    question: "Guess the type of risk that affects a company's public image.",
    answer: "reputational risk",
    clue: "It pertains to public trust and reputation."
  },
  {
    question: "Guess the type of risk associated with digital threats.",
    answer: "cyber risk",
    clue: "It involves IT security and cyberattacks."
  },
];

const RiskSequenceGame = ({ onBack }) => {
  const [gameState, setGameState] = useState('idle'); // idle, running, paused, finished.
  const [correctCount, setCorrectCount] = useState(0);
  const [timer, setTimer] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [clueCoins, setClueCoins] = useState(3);
  const [clueVisible, setClueVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Timer effect: Increase timer every second while game is running.
  useEffect(() => {
    let interval = null;
    if (gameState === 'running') {
      interval = setInterval(() => setTimer(prev => prev + 1), 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameState]);

  const startGame = () => {
    setGameState('running');
    setTimer(0);
    setCorrectCount(0);
    setUserAnswer('');
    setClueCoins(3);
    setClueVisible(false);
    setErrorMessage('');
  };

  const pauseGame = () => gameState === 'running' && setGameState('paused');
  const continueGame = () => gameState === 'paused' && setGameState('running');

  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    const currentQuestion = riskQuestions[correctCount];
    if (userAnswer.trim().toLowerCase() === currentQuestion.answer.toLowerCase()) {
      setCorrectCount(correctCount + 1);
      setUserAnswer('');
      setClueVisible(false);
      setErrorMessage('');
      if (correctCount + 1 === riskQuestions.length) {
        setGameState('finished');
      }
    } else {
      setErrorMessage('Incorrect. Please try again!');
    }
  };

  const handleUseClue = () => {
    if (clueCoins > 0 && !clueVisible) {
      setClueVisible(true);
      setClueCoins(clueCoins - 1);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white shadow rounded-lg my-6">
      <h1 className="text-2xl font-bold text-center mb-4">Risk Wheel Game</h1>
      <button onClick={onBack} className="mb-4 text-blue-500 hover:underline">&larr; Back to Menu</button>

      {gameState === 'idle' && (
        <div className="text-center">
          <button onClick={startGame} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Start Game
          </button>
        </div>
      )}

      {gameState === 'running' && (
        <div>
          <div className="flex justify-between mb-2">
            <p className="font-semibold">Timer: {timer} sec</p>
            <p className="font-semibold">Clue Coins: {clueCoins}</p>
          </div>
          <div className="mb-4">
            <button onClick={pauseGame} className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600">
              Pause
            </button>
          </div>
          <h2 className="text-xl font-semibold mb-2">Question {correctCount + 1} of {riskQuestions.length}:</h2>
          <p className="mb-4">{riskQuestions[correctCount].question}</p>
          {clueVisible && (
            <p className="text-blue-500 italic mb-2">
              Clue: {riskQuestions[correctCount].clue}
            </p>
          )}
          <button
            onClick={handleUseClue}
            disabled={clueCoins === 0 || clueVisible}
            className="mb-4 bg-purple-500 text-white px-3 py-1 rounded hover:bg-purple-600 disabled:opacity-50"
          >
            {clueCoins === 0 ? "No Clue Coins Left" : clueVisible ? "Clue Used" : "Use Clue"}
          </button>
          <form onSubmit={handleSubmitAnswer} className="flex flex-col gap-2">
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Your answer"
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <button type="submit" className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
              Submit Answer
            </button>
          </form>
          {errorMessage && (
            <p className="text-red-500 mt-2">{errorMessage}</p>
          )}
        </div>
      )}

      {gameState === 'paused' && (
        <div className="text-center">
          <p className="mb-4">Game Paused.</p>
          <button onClick={continueGame} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Continue Game
          </button>
        </div>
      )}

      {gameState === 'finished' && (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">Game Finished!</h2>
          <p className="mb-2">You completed the Risk Wheel Puzzle!</p>
          <p className="mb-4">Total Time: {timer} seconds</p>
          <button onClick={startGame} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Restart Game
          </button>
        </div>
      )}

      {/* Risk Wheel Puzzle as a circular layout */}
      <div className="relative w-64 h-64 mx-auto my-6">
        {riskQuestions.map((q, index) => {
          const angleDeg = index * 60 - 90; // Spread evenly, starting at top.
          const angleRad = angleDeg * (Math.PI / 180);
          const center = 128; // 256/2 (container is w-64 which is 16rem ≈ 256px)
          const radius = 90; // radius in px
          const left = center + radius * Math.cos(angleRad) - 32; // subtract half of segment width (w-16 ≈ 64px /2 = 32)
          const top = center + radius * Math.sin(angleRad) - 32;
          return (
            <div
              key={index}
              className="absolute w-16 h-16 rounded-full border-2 border-gray-400 flex items-center justify-center bg-gray-200"
              style={{ left: `${left}px`, top: `${top}px` }}
            >
              {index < correctCount ? (
                <span className="text-xs md:text-sm font-bold text-center px-1">{q.answer}</span>
              ) : (
                <span className="text-2xl font-bold">?</span>
              )}
            </div>
          );
        })}
        {/* Center piece: When completed, display a success message */}
        <div className="absolute inset-0 flex items-center justify-center">
          {correctCount === riskQuestions.length && (
            <div className="text-green-600 font-bold text-xl">Risk Wheel Complete!</div>
          )}
        </div>
      </div>
      <small className="text-gray-500 block text-center mt-2">
        Complete the Risk Wheel Puzzle by correctly guessing all the risk types.
      </small>
    </div>
  );
};

export default RiskSequenceGame;
